import PaywallView from '@components/Projects/PaywallView.js';
import ProjectGridSkeleton from '@components/Projects/skeleton/ProjectGridSkeleton.js';
import ProjectListSkeleton from '@components/Projects/skeleton/ProjectListSkeleton.js';
import ProjectsState from '@states/Projects.js';
import UserState from '@states/User.js';
import { UIDiv } from '@ui';
import GridView from './GridView/index.js';
import ListView from './ListView/index.js';

export default class ProjectContainer extends UIDiv {
  constructor({
    viewMode = 'list',
    accessControl,
    selectedProjectType,
    currentFolderId = null,
    isAnonymousView = false,
  }) {
    super();
    this.setId('project-container');
    this.addClass('flex flex-col w-full flex-1 overflow-auto');
    this.projects = ProjectsState.projects;
    this.viewMode = viewMode;
    this.accessControl = accessControl;
    this.selectedProjectType = selectedProjectType;
    const isLoggedIn = !!UserState.user;
    this.selectedOrg = isLoggedIn ? UserState.user.selectedOrg : null;
    this.currentFolderId = currentFolderId;
    this.isAnonymousView = isAnonymousView;
    this.searchTerm = '';
    this.isShowingSkeleton = false;

    // Listen for loading state events from child views
    this.handleLoadingState = this.handleLoadingState.bind(this);
    this.dom.addEventListener('projectListLoading', this.handleLoadingState);

    this.initializeComponents();
  }

  /**
   * Handle loading state events from ListView/GridView
   * @param {CustomEvent} event - The loading state event
   */
  handleLoadingState(event) {
    const { isLoading, folderId, canCreate, sharedProjectId } = event.detail;
    if (isLoading) {
      this.#showLoadingSkeleton({ folderId, canCreate, sharedProjectId });
    } else {
      this.#hideLoadingSkeleton();
    }
  }

  /**
   * Show loading skeleton - hides all content and shows skeleton
   * @param {Object} options - Options for the skeleton
   * @param {string} options.folderId - The folder ID being loaded (null for root)
   * @param {boolean} options.canCreate - Whether user can create items
   * @param {string} options.sharedProjectId - Shared project ID if in shared view
   */
  #showLoadingSkeleton({
    folderId = null,
    canCreate = true,
    sharedProjectId = null,
  } = {}) {
    if (this.isShowingSkeleton) return;
    this.isShowingSkeleton = true;

    // Hide all current children
    Array.from(this.dom.children).forEach(child => {
      child.style.display = 'none';
    });

    // Create and show skeleton based on view mode and context
    if (this.viewMode === 'grid') {
      this.loadingSkeleton = new ProjectGridSkeleton({ itemCount: 8 });
    } else {
      // Loading root projects
      this.loadingSkeleton = new ProjectListSkeleton({
        rowCount: 8,
        folderId,
        canCreate,
        sharedProjectId,
      });
    }
    this.loadingSkeleton.addClass('flex-1');
    this.add(this.loadingSkeleton);
  }

  /**
   * Hide loading skeleton - removes skeleton and shows content
   */
  #hideLoadingSkeleton() {
    if (!this.isShowingSkeleton) return;
    this.isShowingSkeleton = false;

    // Remove skeleton
    if (this.loadingSkeleton) {
      this.loadingSkeleton.dom.remove();
      this.loadingSkeleton = null;
    }

    // Show all children again
    Array.from(this.dom.children).forEach(child => {
      child.style.display = '';
    });
  }

  initializeComponents(search) {
    this.searchTerm = search || this.searchTerm;

    // Always get fresh projects data from state
    this.projects = ProjectsState.projects;

    // If projects are still loading, don't render anything yet (keep showing loader)
    if (!this.projects || this.projects.isLoading) return;
    // Check for paywall condition first
    if (
      this.selectedProjectType !== 'personal' &&
      this.projects.subscriptionError
    ) {
      this.clear();
      this.#createPayWallView();
      return;
    }

    // Check if there are any projects - we need to check the folders array specifically
    // Also check if we have a currentFolderId with tasks (for direct folder navigation)
    const hasFolders =
      this.projects.folders && this.projects.folders.length > 0;
    const hasTasksForCurrentFolder =
      this.currentFolderId &&
      this.projects.tasks &&
      this.projects.tasks.some(task => task.parentId === this.currentFolderId);

    if (!hasFolders && !hasTasksForCurrentFolder) {
      this.clear();
      this.#createNoProjectsView();
      return;
    }

    // Check if we already have a valid view component that doesn't need to be recreated
    const hasExistingView = this.listViewContainer || this.gridViewContainer;
    const currentContent = this.container?.children.length > 0;

    // Only do full cleanup and recreation if we don't have valid components already
    if (!hasExistingView || !currentContent) {
      // Reset currentFolderId to null for new initialization
      // Cleanup existing components before creating new ones
      if (this.listViewContainer && this.listViewContainer.destroy) {
        this.currentFolderId = null;
        this.listViewContainer.destroy();
      }
      if (this.gridViewContainer && this.gridViewContainer.destroy) {
        this.gridViewContainer.destroy();
      }

      this.clear();
    }
    // Set the initial view based on the viewMode
    if (this.viewMode === 'list') {
      this.listViewContainer = this.#createListViewContainer();
      this.add(this.listViewContainer);
    } else {
      this.gridViewContainer = this.#createGridViewContainer();
      this.add(this.gridViewContainer);
    }
  }

  #createListViewContainer() {
    const listViewContainer = new ListView(
      this.accessControl,
      this.currentFolderId,
      this.selectedProjectType,
      this.isAnonymousView,
      this.searchTerm,
    );
    this.listViewContainer = listViewContainer; // Store reference for search
    return listViewContainer;
  }

  /**
   * Handle search input from the header
   * @param {string} query - The search query
   */
  handleSearch(query) {
    if (this.listViewContainer && this.viewMode === 'list') {
      this.listViewContainer.handleSearch(query);
    }
    if (this.gridViewContainer && this.viewMode === 'grid') {
      this.gridViewContainer.handleSearch(query);
    }
  }

  #createGridViewContainer() {
    const gridViewContainer = new GridView(
      this.accessControl,
      this.selectedProjectType,
      this.searchTerm,
    );
    return gridViewContainer;
  }

  #createNoProjectsView() {
    const noProjectsDiv = new UIDiv().addClass(
      'no-projects-view flex items-center justify-center h-[80%] text-gray-500 text-lg text-center',
    );
    if (this.accessControl.hasPermission('create')) {
      noProjectsDiv.setTextContent(i18next.t('pages.projects.noProjectsFound'));
    } else {
      noProjectsDiv.setTextContent(
        i18next.t('pages.projects.noProjectsFoundUser'),
      );
    }
    this.add(noProjectsDiv);
  }

  #createPayWallView() {
    const paywallView = new PaywallView({
      accessControl: this.accessControl,
      orgId: this.selectedOrg,
      subscriptionError: this.projects.subscriptionError,
    });
    paywallView.addClass('paywall-view');
    this.add(paywallView);
  }

  /**
   * Update access control and re-initialize components
   * Call this when project type changes
   */
  updateAccessControl(accessControl) {
    this.accessControl = accessControl;

    // Update existing view containers if they exist
    if (this.listViewContainer && this.listViewContainer.updateAccessControl) {
      this.listViewContainer.updateAccessControl(accessControl);
      // Also update the selected project type
      this.listViewContainer.selectedProjectType = this.selectedProjectType;
    }

    if (this.gridViewContainer && this.gridViewContainer.updateAccessControl) {
      this.gridViewContainer.updateAccessControl(accessControl);
      // Also update the selected project type
      this.gridViewContainer.selectedProjectType = this.selectedProjectType;
    }

    // Also force a complete re-initialization to ensure all components get the new access control
    this.initializeComponents();
  }

  /**
   * Cleanup method to properly destroy child components and their subscriptions
   */
  destroy() {
    // Clean up loading state event listener
    if (this.handleLoadingState) {
      this.dom.removeEventListener(
        'projectListLoading',
        this.handleLoadingState,
      );
    }

    // Clean up event listeners
    if (this.handlePreviewResize) {
      window.removeEventListener(
        'previewSidebarEvent',
        this.handlePreviewResize,
      );
    }
    if (this.handleWindowResize) {
      window.removeEventListener('resize', this.handleWindowResize);
    }

    // Unsubscribe from ProjectsState changes
    if (this.subscribeFromProjectsState) {
      this.subscribeFromProjectsState();
    }

    if (this.listViewContainer && this.listViewContainer.destroy) {
      this.listViewContainer.destroy();
    }
    if (this.gridViewContainer && this.gridViewContainer.destroy) {
      this.gridViewContainer.destroy();
    }

    // Clean up skeleton
    if (this.loadingSkeleton) {
      this.loadingSkeleton.dom.remove();
      this.loadingSkeleton = null;
    }

    // Clear references
    this.listViewContainer = null;
    this.gridViewContainer = null;
    this.projects = null;
    this.accessControl = null;
    this.lastProjectsHash = null;
  }
}
